import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { Typography } from '@mui/material';
import { useCallback } from 'react';
import {
    type RVAdvisorInvitationApi,
    useCancelAdvisorRvInvitation,
    useCreateAdvisorRvInvitation,
    useDistributorsAndAdvisors,
} from '/@/hooks/nodes';
import { useIsValidatedAdvisor } from '/@/hooks/session';
import { _ } from '/@/locale';
import { InvitationListFrame, InvitationRow, type InvitationStatusConfig } from './advisor-invitation-list-common';

export const useInvitationStatus = () => {
    return {
        pending: {
            label: _('Solicitud pendiente'),
            icon: <PendingActionsIcon sx={{ width: 20, height: 20 }} />,
            descriptionText: _(
                'Una vez que el RV acepte tu solicitud, ambos podrán trabajar en conjunto sobre la misma información de los productores Premium que comparten, crear y ver las mismas recomendaciones y acceder a los datos de los lotes y su rendimiento.'
            ),
        },
        declined: {
            label: _('No aceptó la solicitud'),
            icon: <PersonRemoveIcon sx={{ width: 20, height: 20 }} color='grey' />,
            descriptionText: _(
                'Si es efectivamente el RV con quien trabajas, contactate y avisale que vas a reenviarle la solicitud para que la acepte. Si en realidad querés vincularte con otro RV, enviá una nueva solicitud.'
            ),
        },
        accepted: {
            label: _('Aceptó la solicitud'),
            icon: <PersonAddIcon color='success' sx={{ width: 20, height: 20 }} />,
            descriptionText: '',
        },
    };
};

const SendedInvitations = ({
    invitations,
    loading = false,
}: {
    invitations: RVAdvisorInvitationApi[];
    loading?: boolean;
}) => {
    const cancelInvitation = useCancelAdvisorRvInvitation();
    const createInvitation = useCreateAdvisorRvInvitation();
    const invitationStatus = useInvitationStatus();

    const handleResendInvitation = useCallback(
        (invitation: RVAdvisorInvitationApi) => {
            const targetNodeId = invitation.invitedBy === 'advisor' ? invitation.rv.id : invitation.advisor.id;
            createInvitation.trigger({ userNodeId: targetNodeId });
        },
        [createInvitation]
    );

    const isValidatedAdvisor = useIsValidatedAdvisor();
    const distributorsAndAdvisorsQuery = useDistributorsAndAdvisors();

    const getStatusConfig: (invitation: RVAdvisorInvitationApi) => InvitationStatusConfig = useCallback(
        (invitation: RVAdvisorInvitationApi) => {
            const matchedAdvisor = distributorsAndAdvisorsQuery.data?.results
                ?.flatMap((distributor) => distributor.children)
                .find((advisor) => advisor.id === invitation.advisor.id);
            const nodeIsAvailable = isValidatedAdvisor || (matchedAdvisor !== undefined && !matchedAdvisor.isLinked);
            return {
                pending: {
                    ...invitationStatus.pending,
                    button: nodeIsAvailable ? (
                        <Typography
                            variant='body1'
                            color='secondary'
                            sx={{ whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                                cancelInvitation.trigger(invitation.id);
                            }}
                        >
                            {_('Cancelar solicitud')}
                        </Typography>
                    ) : undefined,
                },
                declined: {
                    ...invitationStatus.declined,
                    button: nodeIsAvailable ? (
                        <Typography
                            variant='body1'
                            color='secondary'
                            sx={{
                                whiteSpace: 'nowrap',
                                cursor: createInvitation.isMutating ? 'not-allowed' : 'pointer',
                                textDecoration: 'underline',
                                opacity: createInvitation.isMutating ? 0.5 : 1,
                            }}
                            onClick={() => {
                                if (!createInvitation.isMutating) {
                                    handleResendInvitation(invitation);
                                }
                            }}
                        >
                            {_('Reenviar solicitud')}
                        </Typography>
                    ) : undefined,
                },
                accepted: {
                    ...invitationStatus.accepted,
                },
            };
        },
        [
            cancelInvitation,
            createInvitation.isMutating,
            handleResendInvitation,
            invitationStatus,
            distributorsAndAdvisorsQuery.data?.results,
            isValidatedAdvisor,
        ]
    );

    return (
        <InvitationListFrame
            loading={loading}
            invitations={invitations}
            emptyIcon={<PersonAddIcon sx={{ width: 40, height: 40, color: 'grey' }} />}
            emptyMessage={_('Aún no enviaste solicitudes de vinculación')}
        >
            {invitations.map((invitation, index) => (
                <InvitationRow
                    key={invitation.id}
                    invitation={invitation}
                    index={index}
                    statusConfig={getStatusConfig(invitation)}
                />
            ))}
        </InvitationListFrame>
    );
};

export default SendedInvitations;
